import saveAs from "file-saver";
import { Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { Component, computed, effect, inject, input } from "@angular/core";

import { TermsType } from "src/app/shared/enums";
import { MembershipsService } from "src/app/features/admin/services";
import { AuthService, LookupsService, Service, SidebarToggleService, cachedResource } from "src/app/core";
import { BreakpointCheckerDirective, OfflineStateComponent, ServiceCardComponent } from "src/app/shared";

@Component({
  selector: "app-services",
  standalone: true,
  imports: [TranslateModule, ServiceCardComponent, BreakpointCheckerDirective, OfflineStateComponent],
  templateUrl: "./services.component.html",
  styleUrl: "./services.component.scss",
})
export class ServicesComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private lookupsService = inject(LookupsService);
  private membershipsService = inject(MembershipsService);
  private sidebarToggleService = inject(SidebarToggleService);

  title = input("SERVICES.SECTION", { alias: "pageTitle" });
  isOverview = input(false);

  services: Service[] = [];
  sortedServices: Service[][] = [];

  readonly #services = cachedResource((context) => this.lookupsService.getReservationServices(context));

  /** What the grid renders: the cards, an offline state, or an honest "there are none". */
  readonly servicesState = this.#services.state;

  readonly #eligibility = cachedResource((context) => this.membershipsService.checkAddingService(context));

  /**
   * Card visibility only. **Unknown means show the card**, hence the `?? 1`: this answer merely hides
   * two membership-gated cards, and the route guard behind them is the real gate — it opens offline
   * for staff (T-11) and redirects otherwise. Hiding a card because we could not check eligibility
   * would take a service away from a clerk who is in fact entitled to it, and leave no trace of why.
   */
  requestStatus = computed(() => this.#eligibility.state().value ?? 1);

  #isSidebarOpened$ = toSignal(this.sidebarToggleService.isSidebarOpened$);

  breakpoints = [
    "(min-width: 720px) and (max-width: 959px)",
    "(min-width: 960px) and (max-width: 1319px)",
    "(min-width: 1320px) and (max-width: 1440px)",
    "(min-width: 1441px)",
  ];
  isMediumScreen: boolean = false;
  isLargeScreen: boolean = false;
  isXLargeScreen: boolean = false;
  isXXLargeScreen: boolean = false;

  get isMobile() {
    return !this.isMediumScreen && !this.isLargeScreen && !this.isXLargeScreen && !this.isXXLargeScreen;
  }

  constructor() {
    // No `if (!services.length) return` guard any more. That early exit was what produced the blank
    // page: an offline clerk got an empty array, the effect bailed, and the grid rendered nothing at
    // all with no explanation. Emptiness is now the template's business to describe, not this
    // effect's business to hide.
    //
    // `loading` is skipped rather than assigned, so pressing Retry does not blank the screen — and
    // with it the retry button — for the length of the round trip. The previous outcome stays put
    // until a new one replaces it.
    effect(() => {
      const services = this.servicesState();
      if (services.status === "loading") return;

      this.assignServices(services.value ?? []);
    });
  }

  retry() {
    this.#services.reload();
    this.#eligibility.reload();
  }

  assignServices(services: Service[]) {
    let numberOfServicesInEachRow = 1;

    if (this.isMediumScreen) {
      numberOfServicesInEachRow = 2;
    } else if (this.isLargeScreen) {
      numberOfServicesInEachRow = 3;
    } else if (this.isXLargeScreen) {
      numberOfServicesInEachRow = this.#isSidebarOpened$() ? 4 : 5;
    } else if (this.isXXLargeScreen) {
      numberOfServicesInEachRow = 5;
    }

    /**
     * Sort services into rows based on the screen size.
     * @param services the services to be sorted
     * @param numberOfServicesInEachRow the number of services in each row
     * @returns an array of arrays, where each inner array contains the services in a row
     */
    this.sortedServices = Array.from({ length: Math.ceil(services.length / numberOfServicesInEachRow) }, (_, index) => {
      const start = index * numberOfServicesInEachRow;
      return services.slice(start, start + numberOfServicesInEachRow);
    });

    this.services = services;
  }

  getBreakpointsStates(layoutStates: Record<string, boolean> | undefined) {
    this.isMediumScreen = layoutStates!["(min-width: 720px) and (max-width: 959px)"];
    this.isLargeScreen = layoutStates!["(min-width: 960px) and (max-width: 1319px)"];
    this.isXLargeScreen = layoutStates!["(min-width: 1320px) and (max-width: 1440px)"];
    this.isXXLargeScreen = layoutStates!["(min-width: 1441px)"];

    if (this.services.length) this.assignServices(this.services);
  }

  logOut() {
    this.authService.logOut();
    this.router.navigate(["/"]);
  }

  isServiceHidden(service: Service, requestStatus: number): boolean {
    const isCompanyOrFriendsService = ["FriendsOfOmanMuseum", "TourismCompanies"].includes(service.code);
    return isCompanyOrFriendsService && requestStatus === 0;
  }

  downloadTermsAndConditions() {
    this.lookupsService.getTermsAndConditions(TermsType.Ticket).subscribe((response: any) => {
      saveAs(response, "terms-and-conditions.pdf");
    });
  }
}
